import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * useScrollController
 * Manages question index, mode (questions/review), and edit-return flow.
 * Also handles accessible focus to a provided headingRef (pass via caller).
 */
export default function useScrollController(params = {}) {
  const {
    numSteps = 0,
    initialIndex = 0,
    initialMode = 'questions', // or 'review'
    headingRef = null,
  } = params;

  const [currentIndex, setCurrentIndex] = useState(
    Math.min(Math.max(0, initialIndex), Math.max(0, numSteps - 1))
  );
  const [mode, setMode] = useState(initialMode === 'review' ? 'review' : 'questions');
  const [editReturnToReview, setEditReturnToReview] = useState(false);
  const [liveRegionText, setLiveRegionText] = useState('');

  // Keep index within bounds when numSteps changes
  useEffect(() => {
    setCurrentIndex((i) => Math.min(Math.max(0, i), Math.max(0, numSteps - 1)));
  }, [numSteps]);

  // Focus and announce heading on index changes in questions mode
  useEffect(() => {
    if (mode !== 'questions') return;
    setLiveRegionText(`Question ${currentIndex + 1} of ${Math.max(1, numSteps)}`);
    const el = headingRef && headingRef.current;
    if (el && typeof el.focus === 'function') {
      try { el.focus({ preventScroll: true }); } catch (_) {}
    }
  }, [currentIndex, mode, numSteps, headingRef]);

  const goNext = useCallback(() => {
    if (mode === 'review') return;
    setCurrentIndex((i) => Math.min(i + 1, Math.max(0, numSteps - 1)));
  }, [mode, numSteps]);

  const goBack = useCallback(() => {
    if (mode === 'review') {
      setMode('questions');
      setCurrentIndex(Math.max(0, numSteps - 1));
      return;
    }
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, [mode, numSteps]);

  const jumpTo = useCallback((index) => {
    const clamped = Math.min(Math.max(0, index), Math.max(0, numSteps - 1));
    setCurrentIndex(clamped);
  }, [numSteps]);

  const beginEditReturn = useCallback((index) => {
    setMode('questions');
    setEditReturnToReview(true);
    jumpTo(index);
  }, [jumpTo]);

  const returnToReview = useCallback(() => {
    setEditReturnToReview(false);
    setMode('review');
  }, []);

  return useMemo(() => ({
    currentIndex,
    mode,
    editReturnToReview,
    liveRegionText,
    setMode,
    goNext,
    goBack,
    jumpTo,
    beginEditReturn,
    returnToReview,
  }), [
    currentIndex,
    mode,
    editReturnToReview,
    liveRegionText,
    goNext,
    goBack,
    jumpTo,
    beginEditReturn,
    returnToReview,
  ]);
}